import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrentTenantService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/tenant/services/current-tenant.service';

@Injectable({
  providedIn: 'root',
})
export class ActivityPingService implements OnDestroy {
  private static readonly PING_INTERVAL_MS = 300000;
  private static readonly RETRY_DELAY_MS = 30000;

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  private activeUserId: string | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly currentTenantService: CurrentTenantService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) {}

  start(userId: string) {
    if (!this.isBrowser() || !userId) {
      return;
    }

    if (this.activeUserId && this.activeUserId !== userId) {
      this.stop();
    }

    if (this.intervalId && this.activeUserId === userId) {
      return;
    }

    if (!this.hasTenantContext()) {
      return;
    }

    this.activeUserId = userId;

    void this.pingNow();

    this.intervalId = setInterval(() => {
      void this.pingNow();
    }, ActivityPingService.PING_INTERVAL_MS);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.inFlight = false;
    this.activeUserId = null;
  }

  async pingNow() {
    if (!this.isBrowser()) {
      return;
    }

    if (!this.activeUserId || this.inFlight || !this.hasTenantContext()) {
      return;
    }

    if (document.hidden || !navigator.onLine) {
      return;
    }

    this.inFlight = true;

    try {
      await firstValueFrom(this.http.post(`${environment.usersServiceApi}me/activity/ping`, null));
    } catch (error: any) {
      console.warn('Activity ping failed.', error);

      if (this.shouldRetry(error)) {
        this.scheduleRetry();
      }
    } finally {
      this.inFlight = false;
    }
  }

  ngOnDestroy() {
    this.stop();
  }

  private scheduleRetry() {
    if (this.retryTimeoutId) {
      return;
    }

    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      void this.pingNow();
    }, ActivityPingService.RETRY_DELAY_MS);
  }

  private shouldRetry(error: { status?: number } | null | undefined): boolean {
    return (error?.status ?? 0) === 0;
  }

  private hasTenantContext(): boolean {
    const currentTenant = this.currentTenantService.currentTenantData;
    const fallbackTenant = this.currentTenantService.currentTenant?.();
    const firebaseTenantId = currentTenant?.firebaseTenantId || fallbackTenant?.firebaseTenantId;

    return !!firebaseTenantId && !!environment.platformId;
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
